import type { ReactNode } from 'react'

export interface HAction {
  id: string
  label: string
  color: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
}

export interface HorizontalActionBarProps {
  actions: HAction[]
  ariaLabel: string
  className?: string
}

/**
 * Horizontal-scrolling row of icon-labelled action tiles.
 *
 * Mobile-first: snaps to each tile on scroll, hides scrollbar chrome, bleeds to
 * viewport edges via `-mx-4 px-4` so the last tile can partially peek off-screen
 * as a scroll affordance. Desktop just wraps a wider row without horizontal
 * overflow being visible.
 *
 * Visual language matches the pre-existing `QuickBtn` grid on TodayScreen
 * (rounded-r-sm, bg-bg-elevated, icon-tile with color+22 halo, active:scale-[0.98])
 * so the extraction is a pure layout swap — same buttons, new arrangement.
 *
 * Extracted as C1.a from the Today layout rework (C1). See `.iron-man/PLANNING-FEED.md`
 * for the sub-chunk spec + parent C1 context.
 */
export function HorizontalActionBar({ actions, ariaLabel, className }: HorizontalActionBarProps) {
  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      className={
        'flex gap-2 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-1 ' +
        '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ' +
        (className ?? '')
      }
    >
      {actions.map((action) => (
        <ActionTile key={action.id} action={action} />
      ))}
    </div>
  )
}

function ActionTile({ action }: { action: HAction }) {
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      className={
        'snap-start shrink-0 basis-[132px] ' +
        'flex flex-col items-start gap-2 p-4 rounded-r-sm ' +
        'bg-bg-elevated border border-divider-subtle ' +
        'active:scale-[0.98] transition-transform min-h-[88px] ' +
        'disabled:opacity-40 disabled:pointer-events-none'
      }
      aria-label={action.label}
    >
      <div
        className="h-9 w-9 rounded-full flex items-center justify-center"
        style={{ background: action.color + '22', color: action.color }}
      >
        {action.icon}
      </div>
      <div className="text-body font-medium text-text-primary text-left">{action.label}</div>
    </button>
  )
}
